/**
 * The supplier portal: both sides of it.
 *
 * **The restaurant's side** (`useSupplierAccess`, `useSupplierInbox`, …) runs
 * in the back office with a staff token, like any other screen.
 *
 * **The supplier's side** (`useSupplierMe`, `useSupplierOrders`, …) runs
 * under its *own* `ApiProvider`, with its own storage key, because a
 * supplier's token is not a staff token: the server refuses it on every staff
 * route and refuses a staff token on every supplier route. It lasts a working
 * day and has no refresh token behind it, so when it runs out the client's
 * ordinary "session over" path sends the person back to the portal's sign-in.
 *
 * Every supplier route names a customer by `linkId`, not a restaurant id —
 * one login can act for two of the same restaurant's suppliers, and each is
 * its own view. Money on the portal is the restaurant's currency: set the
 * display format from the customer before rendering prices.
 *
 * Nothing a supplier sends changes anything by itself. An amendment waits for
 * the restaurant (`decision: 'pending'`), a price waits in the restaurant's
 * queue of proposed prices, and an invoice is checked like a paper one. The
 * server totals an invoice from its lines; the screen never adds up money.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi, useSession, useStartSession } from './provider';

// --------------------------------------------------------------------------- //
// Shapes
// --------------------------------------------------------------------------- //

export type SupplierResponse = 'confirmed' | 'amended' | 'rejected';
export type SupplierDecision = 'pending' | 'accepted' | 'declined' | 'superseded';

export interface SupplierInvitation {
  id: string;
  supplier_id: string;
  email: string;
  expires_at: string;
  sent_at: string;
}

export interface SupplierLogin {
  /** The link's id: what "remove" takes. */
  id: string;
  email: string;
  name: string;
  since: string;
}

export interface SupplierAccess {
  supplier_id: string;
  supplier_name: string;
  /** The address on file for the supplier, to invite without retyping it. */
  supplier_email: string | null;
  logins: SupplierLogin[];
  invitations: SupplierInvitation[];
}

export interface SupplierAnswerLine {
  stock_item_id: string;
  name: string;
  unit: string;
  ordered_quantity: string;
  ordered_price: string;
  quantity: string;
  unit_price: string;
  /** A substitution, a brand, "short until Thursday" — in the supplier's words. */
  note: string | null;
}

export interface SupplierAnswer {
  id: string;
  purchase_order_id: string;
  order_number: number;
  supplier_id: string;
  supplier_name: string;
  response: SupplierResponse;
  /** Only an amendment ever waits; a yes or a no reads as accepted. */
  decision: SupplierDecision;
  answered_by: string;
  answered_at: string;
  note: string | null;
  delivery_on: string | null;
  decided_at: string | null;
  /** Only the lines the supplier changed or commented on. */
  lines: SupplierAnswerLine[];
}

export interface SentPrice {
  /** The price change's id: agree or refuse it with `useDecidePrice`. */
  id: string;
  supplier_id: string;
  supplier_name: string;
  supplier_item_id: string;
  stock_item_name: string;
  from_price: string;
  to_price: string;
  effective_from: string;
  /** proposed, approved, rejected. */
  state: string;
  note: string | null;
  /** Null for a change somebody at the restaurant typed in. */
  submitted_by: string | null;
  submitted_at: string | null;
}

export interface SentInvoice {
  id: string;
  supplier_id: string;
  supplier_name: string;
  purchase_order_id: string | null;
  order_number: number | null;
  reference: string;
  invoice_date: string;
  due_on: string | null;
  total: string;
  /** received, matched, disputed, approved, paid. */
  state: string;
  delivery_note: string | null;
  /** Null for an invoice the restaurant typed in from paper. */
  submitted_by: string | null;
}

export interface SupplierInbox {
  /** Changed orders to take or turn down, and orders the supplier cannot do. */
  answers: SupplierAnswer[];
  prices: SentPrice[];
  /** Invoices from the portal that nobody has checked yet. */
  invoices: SentInvoice[];
}

/** A restaurant, as the supplier signed in sees it. */
export interface SupplierCustomer {
  link_id: string;
  restaurant: string;
  /** Which of the restaurant's suppliers this login acts as. */
  supplier_name: string;
  currency: string;
  locale: string;
  can_answer: boolean;
  can_send_prices: boolean;
  can_send_invoices: boolean;
}

export interface SupplierMe {
  name: string;
  email: string;
  customers: SupplierCustomer[];
}

export interface SupplierOrderLine {
  stock_item_id: string;
  name: string;
  unit: string;
  quantity: string;
  unit_price: string;
  received_quantity: string;
  line_total: string;
}

export type SupplierOrderState = 'approved' | 'receiving' | 'closed' | 'cancelled';

export interface SupplierOrder {
  id: string;
  number: number;
  state: SupplierOrderState;
  deliver_to: string;
  expected_on: string | null;
  note: string | null;
  total: string;
  placed_at: string | null;
  lines: SupplierOrderLine[];
  /** Their latest answer, if any. */
  answer: SupplierAnswer | null;
  can_answer: boolean;
  can_invoice: boolean;
}

export interface SupplierListPrice {
  supplier_item_id: string;
  name: string;
  unit: string;
  supplier_sku: string | null;
  pack_size: string;
  /** What the restaurant pays today. */
  pack_price: string;
  next_price: string | null;
  next_price_from: string | null;
}

export interface SupplierPriceList {
  items: SupplierListPrice[];
  /** Every change on their list, whoever proposed it, newest first. */
  changes: SentPrice[];
}

export interface InvitationLook {
  restaurant: string;
  supplier_name: string;
  email: string;
  /** Whether this address already signs in: asks for its password, not a new one. */
  has_login: boolean;
}

interface SupplierToken {
  access_token: string;
  token_type: string;
  expires_in: number;
  account_id: string;
}

/** In the words of the person at the restaurant. */
export const SUPPLIER_RESPONSE_LABELS: Record<SupplierResponse, string> = {
  confirmed: 'Confirmed',
  amended: 'Offered changes',
  rejected: 'Cannot supply',
};

export const SUPPLIER_DECISION_LABELS: Record<SupplierDecision, string> = {
  pending: 'Waiting on you',
  accepted: 'Taken',
  declined: 'Turned down',
  superseded: 'Answered again',
};

// --------------------------------------------------------------------------- //
// The restaurant's side
// --------------------------------------------------------------------------- //

// Under `purchasing`, so agreeing a price or recording an invoice through the
// purchasing hooks refreshes what is waiting here too.
const KEY = ['purchasing', 'supplier-portal'] as const;

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['purchasing'] });
  };
}

export function useSupplierAccess(enabled = true) {
  const api = useApi();
  return useQuery<SupplierAccess[], ApiError>({
    queryKey: [...KEY, 'access'],
    queryFn: ({ signal }) => api.get<SupplierAccess[]>('/v1/supplier-portal/access', signal),
    enabled,
  });
}

/** Email somebody at a supplier a link into the portal. 502 if the mail would not go. */
export function useInviteSupplier() {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation<SupplierInvitation, ApiError, { supplierId: string; email: string }>({
    mutationFn: ({ supplierId, email }) =>
      api.post<SupplierInvitation>('/v1/supplier-portal/invitations', {
        supplier_id: supplierId,
        email,
      }),
    onSettled: invalidate,
  });
}

export function useWithdrawSupplierInvitation() {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation<void, ApiError, { invitationId: string }>({
    mutationFn: ({ invitationId }) =>
      api.post<void>(`/v1/supplier-portal/invitations/${invitationId}/withdraw`),
    onSettled: invalidate,
  });
}

/** Take a login's access away. From its very next request. */
export function useRemoveSupplierLogin() {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation<void, ApiError, { loginId: string }>({
    mutationFn: ({ loginId }) => api.post<void>(`/v1/supplier-portal/logins/${loginId}/remove`),
    onSettled: invalidate,
  });
}

export function useSupplierInbox(enabled = true) {
  const api = useApi();
  return useQuery<SupplierInbox, ApiError>({
    queryKey: [...KEY, 'inbox'],
    queryFn: ({ signal }) => api.get<SupplierInbox>('/v1/supplier-portal/inbox', signal),
    enabled,
  });
}

/** Every answer the supplier gave to one order, newest first. */
export function useSupplierAnswers(orderId: string | undefined, enabled = true) {
  const api = useApi();
  return useQuery<SupplierAnswer[], ApiError>({
    queryKey: [...KEY, 'answers', orderId ?? null],
    queryFn: ({ signal }) =>
      api.get<SupplierAnswer[]>(`/v1/supplier-portal/orders/${orderId}/answers`, signal),
    enabled: enabled && orderId !== undefined,
  });
}

/** Take what the supplier offered instead (the order's lines change), or turn it down. */
export function useDecideSupplierAnswer() {
  const api = useApi();
  const invalidate = useInvalidate();
  return useMutation<SupplierAnswer, ApiError, { answerId: string; accept: boolean }>({
    mutationFn: ({ answerId, accept }) =>
      api.post<SupplierAnswer>(
        `/v1/supplier-portal/answers/${answerId}/${accept ? 'accept' : 'decline'}`,
      ),
    onSettled: invalidate,
  });
}

// --------------------------------------------------------------------------- //
// The supplier's side: getting in
// --------------------------------------------------------------------------- //

function sessionFrom(token: SupplierToken) {
  return {
    access: token.access_token,
    refresh: null,
    expiresAt: Date.now() + token.expires_in * 1000,
  };
}

export function useSupplierSignIn() {
  const api = useApi();
  const start = useStartSession();
  return useMutation<SupplierToken, ApiError, { email: string; password: string }>({
    // A wrong password is 401, and that is an answer about what was typed.
    mutationFn: (input) =>
      api.post<SupplierToken>('/v1/supplier/sign-in', input, {
        unauthenticatedEndsSession: false,
      }),
    onSuccess: (token) => start(sessionFrom(token)),
  });
}

/** Who is inviting, before the person opening the link decides anything. */
export function useInvitationLook(link: { restaurant: string; token: string } | null) {
  const api = useApi();
  return useQuery<InvitationLook, ApiError>({
    queryKey: ['supplier', 'invitation', link?.token ?? null],
    queryFn: () =>
      api.post<InvitationLook>(
        '/v1/supplier/invitation',
        { restaurant: link?.restaurant, token: link?.token },
        { unauthenticatedEndsSession: false },
      ),
    enabled: link !== null,
    staleTime: Infinity,
  });
}

/**
 * Use an invitation: a new login (name and a password of their choosing), or
 * this restaurant added to the login they have (their existing password).
 */
export function useAcceptInvitation() {
  const api = useApi();
  const start = useStartSession();
  return useMutation<
    SupplierToken,
    ApiError,
    { restaurant: string; token: string; password: string; name?: string }
  >({
    mutationFn: (input) =>
      api.post<SupplierToken>('/v1/supplier/invitation/accept', input, {
        unauthenticatedEndsSession: false,
      }),
    onSuccess: (token) => start(sessionFrom(token)),
  });
}

export function useSupplierMe() {
  const api = useApi();
  const session = useSession();
  return useQuery<SupplierMe, ApiError>({
    queryKey: ['supplier', 'me', session.epoch],
    queryFn: ({ signal }) => api.get<SupplierMe>('/v1/supplier/me', signal),
    enabled: session.isAuthenticated,
  });
}

// --------------------------------------------------------------------------- //
// The supplier's side: one customer
// --------------------------------------------------------------------------- //

function customerPath(linkId: string) {
  return `/v1/supplier/customers/${linkId}`;
}

function useInvalidateCustomer(linkId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['supplier', 'customer', linkId] });
  };
}

export function useSupplierOrders(linkId: string) {
  const api = useApi();
  return useQuery<SupplierOrder[], ApiError>({
    queryKey: ['supplier', 'customer', linkId, 'orders'],
    queryFn: ({ signal }) => api.get<SupplierOrder[]>(`${customerPath(linkId)}/orders`, signal),
  });
}

export interface AnswerLineInput {
  stock_item_id: string;
  /** Decimal strings. Leave out what does not change. */
  quantity?: string;
  unit_price?: string;
  note?: string;
}

export interface AnswerInput {
  orderId: string;
  response: SupplierResponse;
  lines?: AnswerLineInput[];
  note?: string;
  /** ISO date they say it will arrive. */
  deliveryOn?: string;
}

/** Yes, no, or what can be done instead. Answering again replaces the answer. */
export function useAnswerOrder(linkId: string) {
  const api = useApi();
  const invalidate = useInvalidateCustomer(linkId);
  return useMutation<SupplierOrder, ApiError, AnswerInput>({
    mutationFn: (input) =>
      api.post<SupplierOrder>(`${customerPath(linkId)}/orders/${input.orderId}/answer`, {
        response: input.response,
        lines: input.lines ?? [],
        note: input.note ?? null,
        delivery_on: input.deliveryOn ?? null,
      }),
    onSettled: invalidate,
  });
}

export function useSupplierPriceList(linkId: string) {
  const api = useApi();
  return useQuery<SupplierPriceList, ApiError>({
    queryKey: ['supplier', 'customer', linkId, 'prices'],
    queryFn: ({ signal }) =>
      api.get<SupplierPriceList>(`${customerPath(linkId)}/prices`, signal),
  });
}

/** A new price from a date. Waits on the restaurant; nothing is charged at it until then. */
export function useSendPrice(linkId: string) {
  const api = useApi();
  const invalidate = useInvalidateCustomer(linkId);
  return useMutation<
    SentPrice,
    ApiError,
    { supplierItemId: string; toPrice: string; effectiveFrom: string; note?: string }
  >({
    mutationFn: (input) =>
      api.post<SentPrice>(`${customerPath(linkId)}/prices`, {
        supplier_item_id: input.supplierItemId,
        to_price: input.toPrice,
        effective_from: input.effectiveFrom,
        note: input.note ?? null,
      }),
    onSettled: invalidate,
  });
}

export function useSupplierInvoices(linkId: string) {
  const api = useApi();
  return useQuery<SentInvoice[], ApiError>({
    queryKey: ['supplier', 'customer', linkId, 'invoices'],
    queryFn: ({ signal }) => api.get<SentInvoice[]>(`${customerPath(linkId)}/invoices`, signal),
  });
}

export interface SendInvoiceInput {
  orderId: string;
  reference: string;
  invoiceDate: string;
  dueOn?: string;
  deliveryNote?: string;
  /** Decimal strings. The server adds them up. */
  lines: { stock_item_id: string; quantity: string; unit_price: string }[];
}

/** An invoice against a delivered order. The total comes back from the server. */
export function useSendInvoice(linkId: string) {
  const api = useApi();
  const invalidate = useInvalidateCustomer(linkId);
  return useMutation<SentInvoice, ApiError, SendInvoiceInput>({
    mutationFn: (input) =>
      api.post<SentInvoice>(`${customerPath(linkId)}/invoices`, {
        order_id: input.orderId,
        reference: input.reference,
        invoice_date: input.invoiceDate,
        due_on: input.dueOn ?? null,
        delivery_note: input.deliveryNote ?? null,
        lines: input.lines,
      }),
    onSettled: invalidate,
  });
}
